import { NavLink, Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { BookOpen, MessageSquareText, Euro, Briefcase, User, Compass, Shield, Layers, LogOut, BarChart3, ClipboardCheck, FileCheck2, Users, Cookie, FlaskConical, Dumbbell, KeyRound, ScrollText, CircleHelp } from "lucide-react";
import { useUser } from "@/context/UserContext";
import { useLang, LanguageToggle } from "@/context/LanguageContext";
import { HoofdMenu } from "@/components/HoofdMenu";
import { WachtwoordWijzigenDialog } from "@/components/WachtwoordWijzigenDialog";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const ADMIN_ROLLEN = ["beheerder", "superbeheerder"];

const NAV = [
  { to: "/", label: "Vraag & antwoord", labelKey: "nav.qa", icon: MessageSquareText, testid: "nav-vraag", active: "bg-violet-600 text-white", color: "text-violet-600" },
  { to: "/salaris", label: "Salarischeck", labelKey: "nav.salaris", mobielLabel: "Salaris\u00ADcheck", icon: Euro, testid: "nav-salaris", active: "bg-emerald-600 text-white", color: "text-emerald-600" },
  { to: "/functie", label: "Functiecheck", labelKey: "nav.functie", mobielLabel: "Functie\u00ADcheck", icon: Briefcase, testid: "nav-functie", active: "bg-amber-500 text-white", color: "text-amber-600" },
  { to: "/tilcheck", label: "Tilcheck", labelKey: "nav.tilcheck", icon: Dumbbell, testid: "nav-tilcheck", active: "bg-cyan-600 text-white", color: "text-cyan-600" },
  { to: "/functiehandboek", label: "Functiehandboek", labelKey: "nav.handboek", mobielLabel: "Handboek", icon: BookOpen, testid: "nav-functiehandboek", active: "bg-orange-700 text-white", color: "text-orange-700" },
  { to: "/cao", label: "CAO-wijzer", labelKey: "nav.caowijzer", mobielLabel: "CAO", icon: ScrollText, testid: "nav-cao-wijzer", active: "bg-amber-700 text-white", color: "text-amber-700" },
  { to: "/mijn", label: "Mijn omgeving", labelKey: "nav.mijn", icon: User, testid: "nav-mijn", active: "bg-rose-600 text-white", color: "text-rose-600", mobiel: false },
  { to: "/mijn?tab=privacy", label: "Privacy & gegevens", labelKey: null, icon: Shield, testid: "nav-privacy", active: "bg-rose-600 text-white", color: "text-rose-600", mobiel: false },
  // PILOTMAATREGEL v2 (2026-07): OR-analyse volledig uit de pilot (ook voor beheerders).
  // Terugzetten: { to: "/or-analyse", label: "OR-analyse", labelKey: "nav.or", icon: ClipboardCheck, testid: "nav-or-analyse", active: "bg-indigo-600 text-white", color: "text-indigo-600", roles: ["or-lid", "beheerder", "superbeheerder"], mobiel: false },
  // Contractcheck tijdelijk verborgen tijdens de pilot (nog in ontwikkeling).
  // { to: "/contractcheck", label: "Contractcheck", labelKey: "nav.contract", icon: FileCheck2, testid: "nav-contractcheck", active: "bg-teal-600 text-white", color: "text-teal-600" },
  { to: "/bronnen", label: "Bronnen", labelKey: "nav.bronnen", icon: Layers, testid: "nav-kennisbank", active: "bg-blue-600 text-white", color: "text-blue-600", adminOnly: true, mobiel: false },
  { to: "/faq", label: "FAQ & Contact", labelKey: null, icon: CircleHelp, testid: "nav-faq-contact", active: "bg-sky-700 text-white", color: "text-sky-700", mobiel: false },
  { to: "/dashboard", label: "Dashboard", labelKey: "nav.dashboard", icon: BarChart3, testid: "nav-dashboard", active: "bg-slate-900 text-white", color: "text-slate-700", superOnly: true, mobiel: false },
  { to: "/gebruikers", label: "Gebruikersbeheer", labelKey: "nav.gebruikers", icon: Users, testid: "nav-gebruikers", active: "bg-slate-900 text-white", color: "text-slate-700", superOnly: true, mobiel: false },
  { to: "/functie-experiment", label: "Functiecheck-experiment", labelKey: "nav.experiment", icon: FlaskConical, testid: "nav-functie-experiment", active: "bg-fuchsia-600 text-white", color: "text-fuchsia-600", adminOnly: true, mobiel: false },
];

const ROL_LABELS = { "or-lid": "OR-lid", beheerder: "Beheerder", superbeheerder: "Superbeheerder", werknemer: "Werknemer" };

// Zelfde groepering als het mobiele ☰-hoofdmenu, zodat mobiel en desktop dezelfde logica delen.
const SIDEBAR_SECTIES = [
  { titel: "Hulpmiddelen", routes: ["/salaris", "/functie", "/tilcheck", "/functiehandboek", "/cao"] },
  { titel: "Informatie", routes: ["/bronnen", "/faq"] },
  { titel: "Mijn account", routes: ["/mijn", "/mijn?tab=privacy"] },
  { titel: "Beheer", routes: ["/dashboard", "/gebruikers", "/functie-experiment"] },
];
const rolLabel = (rol) => ROL_LABELS[rol] || "Werknemer";

const RoleSwitcher = () => {
  const { user, users, switchUser } = useUser();
  if (!user) return null;
  return (
    <Select value={user.id} onValueChange={switchUser}>
      <SelectTrigger data-testid="role-switcher" className="w-auto h-9 border-slate-300 bg-white text-xs gap-2">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {users.map((u) => (
          <SelectItem key={u.id} value={u.id} data-testid={`role-option-${u.rol}`}>
            {u.naam} · {rolLabel(u.rol)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

const AccountControls = () => {
  const { authEnabled, logout } = useUser();
  const { t } = useLang();
  if (authEnabled) {
    return (
      <Button data-testid="logout-btn" onClick={logout} variant="ghost" size="sm" className="h-9 text-slate-500 hover:text-slate-800 hover:bg-slate-100">
        <LogOut className="w-4 h-4 mr-1.5" /> {t("nav.logout")}
      </Button>
    );
  }
  return <RoleSwitcher />;
};

const Layout = ({ children }) => {
  const location = useLocation();
  const { user, authEnabled } = useUser();
  const { t } = useLang();
  const [wwDialoog, setWwDialoog] = useState(false);
  // Zelfde zichtbaarheidsconditie als het mobiele ☰-menu (HoofdMenu.jsx).
  const toonWachtwoord = authEnabled && user?.auth_provider !== "google";
  const visibleNav = NAV.filter((i) =>
    (!i.adminOnly || ADMIN_ROLLEN.includes(user?.rol)) &&
    (!i.superOnly || user?.rol === "superbeheerder") &&
    (!i.roles || i.roles.includes(user?.rol)));

  return (
    <div className="min-h-screen bg-[#FAF6EE] flex flex-col md:flex-row">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0 border-r border-[#EBE1D2] bg-white">
        <div className="px-6 py-6 border-b border-[#EBE1D2]">
          <Link to="/" data-testid="logo-home-link-desktop" aria-label="Naar het hoofdscherm" className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl zk-brand-gradient flex items-center justify-center shadow-sm">
              <Compass className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-heading font-bold text-[#3A281B] leading-tight">Zoetwaren</p>
              <p className="font-heading font-bold text-[#3A281B] leading-tight">Kompas</p>
            </div>
          </Link>
        </div>
        <nav className="flex-1 px-3 py-4 overflow-y-auto">
          {(() => {
            const itemActief = (item) => {
              const [pad, query] = item.to.split("?");
              if (location.pathname !== pad) return false;
              const wilPrivacy = (query || "").includes("tab=privacy");
              const heeftPrivacy = location.search.includes("tab=privacy");
              return wilPrivacy === heeftPrivacy;
            };
            const NavItem = ({ item }) => {
              const Icon = item.icon;
              const active = itemActief(item);
              return (
                <NavLink to={item.to} data-testid={item.testid}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    active ? item.active : "text-slate-600 hover:bg-slate-100"
                  }`}>
                  <Icon className={`w-4.5 h-4.5 ${active ? "text-white" : item.color}`} /> {item.labelKey ? t(item.labelKey) : item.label}
                </NavLink>
              );
            };
            const vraagItem = visibleNav.find((i) => i.to === "/");
            return (
              <>
                {vraagItem && <div className="mb-4"><NavItem item={vraagItem} /></div>}
                {SIDEBAR_SECTIES.map((sectie) => {
                  const items = sectie.routes
                    .map((r) => visibleNav.find((i) => i.to === r))
                    .filter(Boolean);
                  if (!items.length) return null;
                  return (
                    <div key={sectie.titel} className="mb-4">
                      <p className="px-3 mb-1 text-[10px] font-bold uppercase tracking-[0.15em] text-[#9C8467]">{sectie.titel}</p>
                      <div className="space-y-0.5">
                        {items.map((item) => <NavItem key={item.to} item={item} />)}
                        {sectie.titel === "Mijn account" && toonWachtwoord && (
                          <button data-testid="sidebar-wachtwoord-wijzigen" onClick={() => setWwDialoog(true)}
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors">
                            <KeyRound className="w-4.5 h-4.5 text-[#B9772B]" /> Wachtwoord wijzigen
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </>
            );
          })()}
        </nav>
        <div className="px-4 py-4 border-t border-[#EBE1D2] text-[11px] text-[#9C8467] flex items-center gap-1.5">
          <Cookie className="w-3.5 h-3.5 text-[#B9772B]" /> AVG-conform · gegevensminimalisatie
        </div>
      </aside>
      <WachtwoordWijzigenDialog open={wwDialoog} onOpenChange={setWwDialoog} />

      {/* Main */}
      <div className="flex-1 md:ml-64 flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="sticky top-0 z-40 bg-[#FAF6EE]/90 backdrop-blur border-b border-[#EBE1D2]">
          <div className="px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
            <div className="flex items-center gap-2 md:hidden">
              <HoofdMenu />
              <Link to="/" data-testid="logo-home-link" aria-label="Naar het hoofdscherm" className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg zk-brand-gradient flex items-center justify-center">
                  <Compass className="w-4.5 h-4.5 text-white" />
                </div>
                <span className="font-heading font-bold text-[#3A281B]">Zoetwaren Kompas</span>
              </Link>
            </div>
            <div className="hidden md:block text-xs uppercase tracking-[0.2em] text-[#B9772B] font-bold">
              Kennis · Signalering · Controle
            </div>
            <div className="flex items-center gap-2">
              <LanguageToggle />
              {user && (
                <span
                  data-testid="current-role"
                  className={`hidden sm:inline text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${
                    user.rol === "or-lid" ? "bg-indigo-600 text-white" : ADMIN_ROLLEN.includes(user.rol) ? "bg-slate-900 text-white" : "bg-slate-200 text-slate-700"
                  }`}
                >
                  {rolLabel(user.rol)}
                </span>
              )}
              <AccountControls />
            </div>
          </div>
        </header>
        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 pb-36 md:pb-10 max-w-5xl w-full mx-auto">
          {children}
        </main>
        <footer data-testid="app-footer" className="px-4 sm:px-6 lg:px-8 pb-32 md:pb-6 max-w-5xl w-full mx-auto text-center">
          <p className="text-[11px] leading-relaxed text-slate-400">
            Zoetwaren Kompas kan fouten maken. Controleer belangrijke informatie altijd aan de hand van de cao, wetgeving, het functiehandboek of andere officiële bronnen.
          </p>
          <p className="text-[11px] text-slate-400 mt-1">
            <a href="/over" data-testid="footer-over" className="underline hover:text-slate-600">Over Zoetwaren Kompas</a>
            <span className="mx-1.5">·</span>
            <a href="/voorwaarden" data-testid="footer-voorwaarden" className="underline hover:text-slate-600">Voorwaarden</a>
            <span className="mx-1.5">·</span>
            <a href="/privacy" data-testid="footer-privacy" className="underline hover:text-slate-600">Privacy</a>
          </p>
        </footer>
      </div>

      {/* Mobile bottom nav — alleen primaire items; secundaire functies zitten in het ☰ hoofdmenu */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#EBE1D2] flex items-start justify-around z-50 md:hidden pt-2 pb-20" style={{ paddingBottom: "calc(5rem + env(safe-area-inset-bottom))" }}>
        {visibleNav.filter((i) => i.mobiel !== false).map((item) => {
          const Icon = item.icon;
          const active = location.pathname === item.to;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`${item.testid}-mobile`}
              className={`flex flex-col items-center justify-center gap-0.5 flex-1 min-w-0 px-0.5 text-[9px] min-[380px]:text-[10px] font-medium transition-colors ${
                active ? item.color : "text-slate-400"
              }`}
            >
              <Icon className={`w-5 h-5 ${active ? item.color : "text-slate-400"}`} />
              <span className="w-full text-center leading-tight break-words">
                {item.mobielLabel || t(item.labelKey).split(" ")[0]}
              </span>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
};

export default Layout;
